import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { defer, Observable, throwError } from 'rxjs';
import { filter, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { UserApiService } from '@api/api/user.service';
import type { DisplayPreferences } from '@api/model/displayPreferences';
import type { DisplayPreferencesResponse } from '@api/model/displayPreferencesResponse';
import type { UpdateDisplayPreferencesRequest } from '@api/model/updateDisplayPreferencesRequest';
import { SharedAuthStateService } from '../../stores/shared-auth-state.service';
import { TenantStore } from '../../stores/tenant.store';
import { formatDatePattern, isValidTimeZone } from '../utils/date-time-format.utils';

const DEFAULTS: DisplayPreferences = {
  timezone: 'UTC', language: 'en', dateFormat: 'MMM dd, yyyy', timeFormat: '12h',
  theme: 'dark', accentColor: 'blue', direction: 'ltr',
};
const DATE_FORMATS = ['MMM dd, yyyy', 'MMM d, yyyy', 'dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd'];

/** Confirmed server preferences only; drafts and prior identities never drive shared clocks. */
@Injectable({ providedIn: 'root' })
export class DateTimePreferencesService {
  private readonly api = inject(UserApiService);
  private readonly auth = inject(SharedAuthStateService);
  private readonly tenant = inject(TenantStore);
  private readonly scope = computed(() => this.auth.isAuthenticated() && this.auth.currentUserId()
    ? JSON.stringify([this.auth.currentUserId(), this.tenant.currentTenant()?.id ?? this.auth.currentTenantId()])
    : null);
  private readonly saved = signal<{ scope: string | null; preferences: DisplayPreferences } | null>(null);
  private pending: { scope: string | null; revision: number; response: Observable<DisplayPreferencesResponse> } | null = null;
  private revision = 0;
  readonly preferences = computed(() => this.saved()?.scope === this.scope()
    ? this.saved()!.preferences : DEFAULTS);
  readonly timezone = computed(() => this.preferences().timezone);
  readonly dateFormat = computed(() => this.preferences().dateFormat);

  constructor() {
    effect(onCleanup => {
      const scope = this.scope();
      this.saved.set(null);
      this.pending = null;
      this.revision++;
      if (!scope) {return;}
      const subscription = this.load().subscribe({ error: () => { /* UTC defaults until an explicit retry. */ } });
      onCleanup(() => subscription.unsubscribe());
    });
  }

  load(): Observable<DisplayPreferencesResponse> {
    return defer(() => {
      const scope = this.scope();
      if (this.pending?.scope === scope && this.pending.revision === this.revision) {return this.pending.response;}
      const revision = this.revision;
      const response = this.api.getDisplayPreferences().pipe(
        map(value => this.normalize(value)),
        filter(() => scope === this.scope() && revision === this.revision),
        tap(value => {
          if (scope === this.scope() && revision === this.revision) {
            this.saved.set({ scope, preferences: value.preferences });
          }
        }),
        finalize(() => {if (this.pending?.response === response) {this.pending = null;}}),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
      this.pending = { scope, revision, response };
      return response;
    });
  }

  save(request: UpdateDisplayPreferencesRequest): Observable<DisplayPreferencesResponse> {
    return defer(() => {
      if (request.timezone != null && !isValidTimeZone(request.timezone)) {
        return throwError(() => new Error('Choose a valid IANA timezone.'));
      }
      const scope = this.scope();
      const revision = ++this.revision;
      this.pending = null;
      return this.api.updateDisplayPreferences(request).pipe(
        map(value => this.normalize(value)),
        filter(() => scope === this.scope() && revision === this.revision),
        tap(value => {
          if (scope === this.scope() && revision === this.revision) {
            this.revision++; // Retire reads started while this write was in flight.
            this.saved.set({ scope, preferences: value.preferences });
          }
        }),
      );
    });
  }

  formatDate(value: Date | string | number | null | undefined, pattern = this.dateFormat()): string {
    return formatDatePattern(value, pattern, this.preferences().language, this.timezone());
  }

  formatTime(value: Date | string | number | null | undefined, seconds = false): string {
    if (value === null || value === undefined || value === '') {return '—';}
    const date = value instanceof Date ? value : new Date(value);
    if (!Number.isFinite(date.getTime())) {return '—';}
    return new Intl.DateTimeFormat(this.preferences().language, {
      timeZone: this.timezone(), hour: '2-digit', minute: '2-digit',
      ...(seconds ? { second: '2-digit' } : {}),
      hourCycle: this.preferences().timeFormat === '24h' ? 'h23' : 'h12',
    }).format(date);
  }

  formatDateTime(value: Date | string | number | null | undefined): string {
    const date = this.formatDate(value);
    return date === '—' ? date : `${date} ${this.formatTime(value)} (${this.timezone()})`;
  }

  private normalize(response: DisplayPreferencesResponse): DisplayPreferencesResponse {
    const preferences = { ...DEFAULTS, ...response.preferences };
    if (!isValidTimeZone(preferences.timezone)) {preferences.timezone = 'UTC';}
    if (!DATE_FORMATS.includes(preferences.dateFormat)) {preferences.dateFormat = DEFAULTS.dateFormat;}
    if (!['12h', '24h'].includes(preferences.timeFormat)) {preferences.timeFormat = DEFAULTS.timeFormat;}
    try {new Intl.DateTimeFormat(preferences.language);} catch {preferences.language = 'en';}
    return { ...response, preferences };
  }
}
